import { QuizAnswers } from '../types/quiz';

// Placeholder function for AI integration
export const sendToAIScenario = async (quizAnswers: QuizAnswers): Promise<string> => {
  // This would be replaced with an actual API call to your AI service
  console.log('Sending to AI scenario:', quizAnswers);
  
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 1500));
  
  // Generate a placeholder tip based on the answers
  if (quizAnswers.platforms.includes('twitter') && quizAnswers.publishFrequency === 'daily') {
    return "Focus on daily Twitter posts with trending hashtags to boost engagement. Short, punchy content performs best.";
  } else if (quizAnswers.platforms.includes('instagram')) {
    return "Your Instagram strategy should focus on high-quality visuals. Consider creating a consistent visual theme for your content.";
  } else if (quizAnswers.platforms.includes('youtube')) {
    return "For YouTube success, focus on searchable titles and thumbnails that drive clicks. Consistency in your upload schedule is key.";
  } else if (quizAnswers.mainGoal === 'monetize') {
    return "To monetize effectively, focus on building a dedicated audience first. Consider creating premium content or digital products.";
  } else if (quizAnswers.contentTypes.includes('blog')) {
    return "For blog content, focus on SEO optimization and creating comprehensive guides that provide real value to your readers.";
  } else {
    return "Based on your preferences, we recommend focusing on consistency and quality. Create a content calendar and stick to it for best results.";
  }
};

// Save quiz results to local storage
export const saveQuizResults = (answers: QuizAnswers): void => {
  localStorage.setItem('quizAnswers', JSON.stringify(answers));
  localStorage.setItem('quizCompleted', 'true');
};

// Get quiz results from local storage
export const getQuizResults = (): QuizAnswers | null => {
  const savedAnswers = localStorage.getItem('quizAnswers');
  if (savedAnswers) {
    return JSON.parse(savedAnswers);
  }
  return null;
};

// Check if quiz is completed
const isQuizCompleted = (): boolean => {
  return localStorage.getItem('quizCompleted') === 'true';
};

// Course progress tracking
interface CourseProgress {
  courseId: string;
  completed: string[];
  lastAccessed: string;
}

export const saveCourseProgress = (courseId: string, lessonId: string) => {
  const progress = getCourseProgress();
  const courseProgress = progress.find(p => p.courseId === courseId);
  
  if (courseProgress) {
    if (!courseProgress.completed.includes(lessonId)) {
      courseProgress.completed.push(lessonId);
      courseProgress.lastAccessed = new Date().toISOString();
    }
  } else {
    progress.push({
      courseId,
      completed: [lessonId],
      lastAccessed: new Date().toISOString()
    });
  }
  
  localStorage.setItem('courseProgress', JSON.stringify(progress));
};

export const getCourseProgress = (): CourseProgress[] => {
  const progress = localStorage.getItem('courseProgress');
  return progress ? JSON.parse(progress) : [];
};

export const getCompletionPercentage = (courseId: string, totalLessons: number): number => {
  const progress = getCourseProgress();
  const courseProgress = progress.find(p => p.courseId === courseId);
  if (!courseProgress) return 0;
  return Math.round((courseProgress.completed.length / totalLessons) * 100);
};

// Reset quiz status (for testing)
const resetQuizStatus = (): void => {
  localStorage.removeItem('quizAnswers');
  localStorage.setItem('quizCompleted', 'false');
  localStorage.removeItem('courseProgress');
};